import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Chip,
  Grid,
  Divider,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  FormLabel,
} from '@mui/material';
import { Save, Cancel, AutoAwesome, ArrowBack, Info, Edit, BarChart } from '@mui/icons-material';
import api from '../services/api';
import { uploadService } from '../services/uploadService';
import { Header } from '../components/layout/Header';

interface ColumnType {
  apiName: string;
  displayName: string;
  dataType: string;
  required: boolean;
}

interface DataCategory {
  value: string;
  displayName: string;
  description: string;
}

interface SheetPreview {
  sheetName: string;
  sheetIndex: number;
  totalRows: number;
  columnHeaders: string[];
  sampleData: string[][];
  columnInfo: ColumnInfo[];
}

interface ColumnInfo {
  name: string;
  index: number;
  detectedType: string;
  nonNullCount: number;
  nullCount: number;
  sampleValues: string[];
}

interface ColumnMapping {
  excelColumnName: string;
  excelColumnIndex: number;
  apiColumnType: string;
  isRequired: boolean;
  defaultValue?: string;
  transformation?: string;
  aggregation?: string; // SUM, AVG, MAX, MIN, FIRST, LAST, CONCAT
}

interface SheetMapping {
  sheetName: string;
  sheetOrder: number;
  dataCategory: string;
  tableName: string;
  skipRows: number;
  columnMappings: ColumnMapping[];
}

interface MappingTemplate {
  name: string;
  description: string;
  isDefault: boolean;
  uploadId?: number;
  sheetMappings: SheetMapping[];
}

const MappingEditorPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const uploadId = searchParams.get('uploadId');
  const templateId = searchParams.get('templateId');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [sheets, setSheets] = useState<SheetPreview[]>([]);
  const [columnTypes, setColumnTypes] = useState<ColumnType[]>([]);
  const [dataCategories, setDataCategories] = useState<DataCategory[]>([]);

  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');

  const [sheetMappings, setSheetMappings] = useState<SheetMapping[]>([]);
  const [activeSheetIndex, setActiveSheetIndex] = useState(0);
  const [editMode, setEditMode] = useState(false); // templateId alapú szerkesztés
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveOption, setSaveOption] = useState<'overwrite' | 'new'>('overwrite');
  const [newTemplateName, setNewTemplateName] = useState('');

  useEffect(() => {
    // Ha templateId és uploadId is van, akkor template szerkesztése fájllal
    if (templateId && uploadId) {
      setEditMode(true);
      loadTemplateWithFile();
    } else if (templateId) {
      // Ha csak templateId van, akkor template szerkesztése fájl nélkül
      setEditMode(true);
      loadExistingTemplate();
    } else if (uploadId) {
      // Ha csak uploadId van, akkor új template létrehozása fájlból
      loadData();
      loadUploadInfo();
    } else {
      setError('Upload ID vagy Template ID szükséges');
      setLoading(false);
    }
  }, [uploadId, templateId]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load Excel preview
      const previewResponse = await api.post('/mappings/preview', {
        uploadId: parseInt(uploadId!),
        maxRows: 10
      });
      const previewData = previewResponse.data;
      setSheets(previewData.sheets || []);

      // Initialize sheet mappings
      const initialMappings: SheetMapping[] = previewData.sheets.map((sheet: SheetPreview, index: number) => ({
        sheetName: sheet.sheetName,
        sheetOrder: index,
        dataCategory: index === 0 ? 'DEBT_CASES' : 'OTHER',
        tableName: sheet.sheetName,
        skipRows: 0,
        columnMappings: sheet.columnHeaders.map((header: string, colIndex: number) => ({
          excelColumnName: header,
          excelColumnIndex: colIndex,
          apiColumnType: '',
          isRequired: false,
          defaultValue: '',
          transformation: '',
          aggregation: ''
        }))
      }));
      setSheetMappings(initialMappings);

      // Load column types
      const typesResponse = await api.get('/mappings/column-types');
      setColumnTypes(typesResponse.data.columnTypes || []);

      // Load data categories
      const categoriesResponse = await api.get('/mappings/data-categories');
      setDataCategories(categoriesResponse.data || []);

      setLoading(false);
    } catch (err: any) {
      console.error('Error loading data:', err);
      setError(err.response?.data?.message || 'Failed to load Excel preview');
      setLoading(false);
    }
  };

  const loadExistingTemplate = async () => {
    try {
      setLoading(true);

      // Load the template data
      const templateResponse = await api.get(`/mappings/templates/${templateId}`);
      const templateData = templateResponse.data;

      // Populate form fields
      setTemplateName(templateData.name || '');
      setTemplateDescription(templateData.description || '');
      setSheetMappings(templateData.sheetMappings || []);

      // Load column types and categories
      const [typesResponse, categoriesResponse] = await Promise.all([
        api.get('/mappings/column-types'),
        api.get('/mappings/data-categories')
      ]);

      setColumnTypes(typesResponse.data.columnTypes || []);
      setDataCategories(categoriesResponse.data || []);

      // Create mock sheet previews based on template data
      const mockSheets: SheetPreview[] = (templateData.sheetMappings || []).map((sm: SheetMapping) => ({
        sheetName: sm.sheetName,
        sheetIndex: sm.sheetOrder,
        totalRows: 0,
        columnHeaders: sm.columnMappings.map((cm: ColumnMapping) => cm.excelColumnName),
        sampleData: [],
        columnInfo: []
      }));
      setSheets(mockSheets);

      setLoading(false);
    } catch (err: any) {
      console.error('Error loading template:', err);
      setError(err.response?.data?.message || 'Failed to load template');
      setLoading(false);
    }
  };

  const loadTemplateWithFile = async () => {
    try {
      setLoading(true);

      // Load template data and file preview in parallel
      const [templateResponse, previewResponse] = await Promise.all([
        api.get(`/mappings/templates/${templateId}`),
        api.post('/mappings/preview', {
          uploadId: parseInt(uploadId!),
          maxRows: 10
        })
      ]);

      const templateData = templateResponse.data;
      const previewData = previewResponse.data;

      // Populate form fields from template
      setTemplateName(templateData.name || '');
      setTemplateDescription(templateData.description || '');

      // Set sheets from file preview (with real data)
      setSheets(previewData.sheets || []);

      // Merge template mappings with actual file columns
      const mergedSheetMappings = previewData.sheets.map((sheet: SheetPreview, sheetIdx: number) => {
        // Find matching template sheet mapping by name
        const templateSheetMapping = templateData.sheetMappings.find(
          (sm: SheetMapping) => sm.sheetName === sheet.sheetName
        );

        if (!templateSheetMapping) {
          // No template for this sheet - create default empty mapping
          return {
            sheetName: sheet.sheetName,
            sheetOrder: sheetIdx,
            dataCategory: sheetIdx === 0 ? 'DEBT_CASES' : 'OTHER',
            tableName: sheet.sheetName,
            skipRows: 0,
            columnMappings: sheet.columnHeaders.map((header: string, colIndex: number) => ({
              excelColumnName: header,
              excelColumnIndex: colIndex,
              apiColumnType: '',
              isRequired: false,
              defaultValue: '',
              transformation: '',
              aggregation: ''
            }))
          };
        }

        // Create a map of template mappings by Excel column name
        const templateMappingsMap = new Map(
          templateSheetMapping.columnMappings.map((cm: ColumnMapping) => [cm.excelColumnName, cm])
        );

        // Create column mappings for ALL columns in the file
        const columnMappings = sheet.columnHeaders.map((header: string, colIndex: number) => {
          const existingMapping = templateMappingsMap.get(header);
          if (existingMapping) {
            // Use existing mapping but ensure excelColumnIndex is correct
            return {
              ...existingMapping,
              excelColumnIndex: colIndex
            };
          } else {
            // New column not in template - create empty mapping
            return {
              excelColumnName: header,
              excelColumnIndex: colIndex,
              apiColumnType: '',
              isRequired: false,
              defaultValue: '',
              transformation: '',
              aggregation: ''
            };
          }
        });

        return {
          sheetName: sheet.sheetName,
          sheetOrder: sheetIdx,
          dataCategory: templateSheetMapping.dataCategory || 'OTHER',
          tableName: templateSheetMapping.tableName || sheet.sheetName,
          skipRows: templateSheetMapping.skipRows || 0,
          columnMappings
        };
      });

      setSheetMappings(mergedSheetMappings);

      // Load column types and categories
      const [typesResponse, categoriesResponse] = await Promise.all([
        api.get('/mappings/column-types'),
        api.get('/mappings/data-categories')
      ]);

      setColumnTypes(typesResponse.data.columnTypes || []);
      setDataCategories(categoriesResponse.data || []);

      setLoading(false);
    } catch (err: any) {
      console.error('Error loading template with file:', err);
      setError(err.response?.data?.message || 'Failed to load template with file preview');
      setLoading(false);
    }
  };

  const updateSheetMapping = (sheetIndex: number, field: string, value: any) => {
    setSheetMappings(prev => {
      const updated = [...prev];
      updated[sheetIndex] = { ...updated[sheetIndex], [field]: value };
      return updated;
    });
  };

  const updateColumnMapping = (sheetIndex: number, colIndex: number, field: string, value: any) => {
    setSheetMappings(prev => {
      const updated = [...prev];
      const sheet = { ...updated[sheetIndex] };
      const columns = [...sheet.columnMappings];
      columns[colIndex] = { ...columns[colIndex], [field]: value };
      sheet.columnMappings = columns;
      updated[sheetIndex] = sheet;
      return updated;
    });
  };

  const autoMapColumns = (sheetIndex: number) => {
    const mapping = sheetMappings[sheetIndex];

    const updatedMappings = mapping.columnMappings.map((colMapping) => {
      const header = colMapping.excelColumnName.toLowerCase();

      // Simple auto-mapping heuristics
      let suggestedType = '';
      if (header.includes('azonosít') || header.includes('id')) {
        suggestedType = 'debt_case_identifier';
      } else if (header.includes('tőke') || header.includes('capital')) {
        suggestedType = 'current_capital';
      } else if (header.includes('kamat') || header.includes('interest')) {
        suggestedType = 'current_interest';
      } else if (header.includes('költség') || header.includes('cost')) {
        suggestedType = 'current_cost';
      } else if (header.includes('adós') || header.includes('debtor') || header.includes('név')) {
        suggestedType = 'debtor_name';
      } else if (header.includes('város') || header.includes('city')) {
        suggestedType = 'residence_city';
      } else if (header.includes('cím') || header.includes('address')) {
        suggestedType = 'residence_address';
      }

      return {
        ...colMapping,
        apiColumnType: suggestedType,
        isRequired: suggestedType === 'debt_case_identifier'
      };
    });

    updateSheetMapping(sheetIndex, 'columnMappings', updatedMappings);
  };

  const loadUploadInfo = async () => {
    if (!uploadId) return;
    try {
      await uploadService.getUpload(parseInt(uploadId));
    } catch (err) {
      console.error('Failed to load upload info:', err);
    }
  };

  const saveTemplate = async () => {
    try {
      if (!templateName.trim()) {
        alert('Kérlek add meg a sablon nevét!');
        return;
      }

      // Validation: csak DEBT_CASES, DEBTORS, TRANSACTIONS adatkörű sheet-eknek kell debt_case_identifier
      for (const mapping of sheetMappings) {
        // OTHER adatkörű sheet-eknek nem kell debt_case_identifier
        if (mapping.dataCategory === 'OTHER') {
          continue;
        }
        const hasIdentifier = mapping.columnMappings.some(
          col => col.apiColumnType === 'debt_case_identifier'
        );
        if (!hasIdentifier) {
          alert(`A "${mapping.sheetName}" lapnak kötelező a "Követelés ügy azonosító" mező!`);
          return;
        }
      }

      // If editing existing template, show save dialog
      if (editMode && templateId) {
        setSaveDialogOpen(true);
        return;
      }

      // Otherwise, save as new template
      await performSave(false);
    } catch (err: any) {
      console.error('Error saving template:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || 'Failed to save template';
      alert(`Hiba történt: ${errorMessage}`);
    }
  };

  const handleSaveDialogConfirm = async () => {
    setSaveDialogOpen(false);

    if (saveOption === 'new' && !newTemplateName.trim()) {
      alert('Kérlek add meg az új sablon nevét!');
      return;
    }

    await performSave(saveOption === 'overwrite');
  };

  const performSave = async (overwrite: boolean) => {
    try {
      setSaving(true);

      const finalTemplateName = overwrite || !newTemplateName.trim() ? templateName : newTemplateName;

      const template: MappingTemplate = {
        name: finalTemplateName,
        description: templateDescription,
        isDefault: false,
        uploadId: uploadId ? parseInt(uploadId) : undefined,
        sheetMappings: sheetMappings
      };

      if (overwrite && editMode && templateId) {
        // Update existing template
        await api.put(`/mappings/templates/${templateId}`, template);
        alert('Mapping sablon sikeresen frissítve!');
      } else {
        // Create new template
        await api.post('/mappings/templates', template);
        alert(`Mapping sablon sikeresen mentve${!overwrite ? ' új néven' : ''}!`);
      }

      navigate('/mappings');
    } catch (err: any) {
      console.error('Error saving template:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || 'Failed to save template';
      alert(`Hiba történt: ${errorMessage}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <>
        <Header />
        <Container maxWidth="xl" sx={{ mt: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={60} sx={{ mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Excel elemzése...
            </Typography>
          </Box>
        </Container>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <Container maxWidth="md" sx={{ mt: 4 }}>
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Alert severity="error" sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Hiba történt
              </Typography>
              <Typography>{error}</Typography>
            </Alert>
            <Button
              variant="contained"
              startIcon={<ArrowBack />}
              onClick={() => navigate(editMode ? '/mappings' : '/auction-files')}
            >
              Vissza
            </Button>
          </Paper>
        </Container>
      </>
    );
  }

  const currentSheet = sheets[activeSheetIndex];
  const currentMapping = sheetMappings[activeSheetIndex];

  return (
    <>
      <Header />
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ mb: 4, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            {editMode ? <><Edit /> Mapping Szerkesztése</> : <><BarChart /> Új Mapping Létrehozása</>}
          </Typography>

          {/* Template Info */}
          <Paper elevation={1} sx={{ p: 3, mb: 4, bgcolor: 'action.hover' }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
              Sablon adatok
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Sablon neve"
                  required
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="pl. MBH tender"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Leírás"
                  value={templateDescription}
                  onChange={(e) => setTemplateDescription(e.target.value)}
                  placeholder="Sablon leírása (opcionális)"
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Sheet Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs
              value={activeSheetIndex}
              onChange={(_, newValue) => setActiveSheetIndex(newValue)}
              variant="scrollable"
              scrollButtons="auto"
            >
              {sheets.map((sheet, index) => (
                <Tab
                  key={index}
                  label={`${sheet.sheetName} (${sheet.totalRows} sor)`}
                />
              ))}
            </Tabs>
          </Box>

          {/* Sheet Configuration */}
          {currentSheet && currentMapping && (
            <Box sx={{ mb: 4 }}>
              <Paper elevation={1} sx={{ p: 3, mb: 3, bgcolor: 'background.default' }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Adatkör *</InputLabel>
                      <Select
                        value={currentMapping.dataCategory}
                        onChange={(e) => updateSheetMapping(activeSheetIndex, 'dataCategory', e.target.value)}
                        label="Adatkör *"
                      >
                        {dataCategories.map(cat => (
                          <MenuItem key={cat.value} value={cat.value}>
                            {cat.displayName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Tábla neve (API)"
                      value={currentMapping.tableName}
                      onChange={(e) => updateSheetMapping(activeSheetIndex, 'tableName', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Kihagyott sorok száma"
                      value={currentMapping.skipRows}
                      onChange={(e) => updateSheetMapping(activeSheetIndex, 'skipRows', parseInt(e.target.value) || 0)}
                      inputProps={{ min: 0 }}
                    />
                  </Grid>
                </Grid>
              </Paper>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Oszlop leképezések
                </Typography>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<AutoAwesome />}
                  onClick={() => autoMapColumns(activeSheetIndex)}
                  size="small"
                >
                  Automatikus leképezés
                </Button>
              </Box>

              {/* Column Mappings Table */}
              <TableContainer component={Paper} elevation={1}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Excel oszlop</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Detektált típus</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>API mező *</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>Kötelező</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        Aggregáció
                        <Tooltip title="Ha több Excel oszlop ugyanarra az API oszlopra van mappolva, itt választható az aggregációs módszer (összeg, átlag, stb.). Csak akkor jelenik meg, ha több oszlop ugyanarra az API mezőre van mappolva.">
                          <IconButton size="small" sx={{ ml: 0.5, p: 0.5 }}>
                            <Info fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Minta értékek</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {currentMapping.columnMappings.map((colMapping, colIdx) => {
                      const colInfo = currentSheet.columnInfo[colMapping.excelColumnIndex];
                      // Check if multiple columns are mapped to the same API column
                      const sameApiColumnCount = currentMapping.columnMappings.filter(
                        (cm) => cm.apiColumnType && cm.apiColumnType === colMapping.apiColumnType
                      ).length;
                      const showAggregation = colMapping.apiColumnType && sameApiColumnCount > 1;

                      return (
                        <TableRow key={colIdx} hover>
                          <TableCell sx={{ fontWeight: 500 }}>
                            {colMapping.excelColumnName}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={colInfo?.detectedType || 'UNKNOWN'}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <FormControl fullWidth size="small">
                              <Select
                                value={colMapping.apiColumnType}
                                onChange={(e) => updateColumnMapping(activeSheetIndex, colIdx, 'apiColumnType', e.target.value)}
                                displayEmpty
                              >
                                <MenuItem value="">
                                  <em>-- Válassz --</em>
                                </MenuItem>
                                {columnTypes.map(type => (
                                  <MenuItem key={type.apiName} value={type.apiName}>
                                    {type.displayName} ({type.dataType})
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell align="center">
                            <Checkbox
                              checked={colMapping.isRequired}
                              onChange={(e) => updateColumnMapping(activeSheetIndex, colIdx, 'isRequired', e.target.checked)}
                            />
                          </TableCell>
                          <TableCell>
                            {showAggregation ? (
                              <FormControl fullWidth size="small">
                                <Select
                                  value={colMapping.aggregation || ''}
                                  onChange={(e) => updateColumnMapping(activeSheetIndex, colIdx, 'aggregation', e.target.value)}
                                  displayEmpty
                                >
                                  <MenuItem value="">
                                    <em>-- Válassz --</em>
                                  </MenuItem>
                                  <MenuItem value="SUM">Összeg (SUM)</MenuItem>
                                  <MenuItem value="AVG">Átlag (AVG)</MenuItem>
                                  <MenuItem value="MAX">Maximum (MAX)</MenuItem>
                                  <MenuItem value="MIN">Minimum (MIN)</MenuItem>
                                  <MenuItem value="FIRST">Első érték (FIRST)</MenuItem>
                                  <MenuItem value="LAST">Utolsó érték (LAST)</MenuItem>
                                  <MenuItem value="CONCAT">Összefűzés (CONCAT)</MenuItem>
                                </Select>
                              </FormControl>
                            ) : (
                              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                -
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                            {colInfo?.sampleValues?.slice(0, 3).join(', ') || '-'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          <Divider sx={{ my: 4 }} />

          {/* Actions */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 2 }}>
            <Button
              variant="outlined"
              startIcon={<Cancel />}
              onClick={() => navigate(editMode ? '/mappings' : '/auction-files')}
            >
              Mégse
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={saving ? <CircularProgress size={20} /> : <Save />}
              onClick={saveTemplate}
              disabled={saving}
              size="large"
            >
              {saving ? 'Mentés...' : 'Sablon mentése'}
            </Button>
          </Box>

          {/* Save Dialog */}
          <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Mapping Sablon Mentése</DialogTitle>
            <DialogContent>
              <Box sx={{ pt: 2 }}>
                <Alert severity="info" sx={{ mb: 3 }}>
                  Szerkesztesz egy meglévő mapping sablont. Válaszd ki, hogyan szeretnéd menteni!
                </Alert>

                <FormControl component="fieldset">
                  <FormLabel component="legend">Mentési opció</FormLabel>
                  <RadioGroup
                    value={saveOption}
                    onChange={(e) => {
                      setSaveOption(e.target.value as 'overwrite' | 'new');
                      if (e.target.value === 'new') {
                        setNewTemplateName(templateName + ' (másolat)');
                      }
                    }}
                  >
                    <FormControlLabel
                      value="overwrite"
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body1" fontWeight={500}>
                            Meglévő sablon felülírása
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            "{templateName}" sablon frissítése
                          </Typography>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      value="new"
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body1" fontWeight={500}>
                            Mentés új sablonként
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Új mapping sablon létrehozása
                          </Typography>
                        </Box>
                      }
                    />
                  </RadioGroup>
                </FormControl>

                {saveOption === 'new' && (
                  <TextField
                    fullWidth
                    label="Új sablon neve"
                    value={newTemplateName}
                    onChange={(e) => setNewTemplateName(e.target.value)}
                    sx={{ mt: 3 }}
                    required
                  />
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSaveDialogOpen(false)}>
                Mégse
              </Button>
              <Button
                onClick={handleSaveDialogConfirm}
                variant="contained"
                startIcon={<Save />}
                disabled={saving}
              >
                {saving ? 'Mentés...' : 'Mentés'}
              </Button>
            </DialogActions>
          </Dialog>
        </Paper>
      </Container>
    </>
  );
};

export default MappingEditorPage;

